<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row">
          <div class="col-8">
            <h3 class="mb-0">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTIONS_TO_MAKE") }}
            </h3>
          </div>
          <div class="col-4 text-right">
            <base-button type="secondary" size="sm" @click="goToOrders">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ESTABLISHMENT_INTERNAL_ORDERS_LIST") }}
            </base-button>
          </div>
        </div>
      </template>

      <div class="col-12">
        <p class="text-muted small mb-3">
          <i class="fas fa-info-circle mr-1"></i>
          {{
            isWeekView
              ? $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEKLY_PREPARATIONS_HELP")
              : $t("ESTABLISHMENT_INTERNAL_ORDERS.DAILY_PREPARATIONS_HELP")
          }}
        </p>

        <!-- One compact row: who produces, which period, which day or week. -->
        <div class="row">
          <div class="col-lg-4 col-md-6">
            <base-input
              :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')} (*)`"
            >
              <production-establishment-selector
                :establishment="production"
                :restrictToIds="allowedProductionIds"
                @establishmentChanged="(id) => (production = id || null)"
                @loaded="onProductionsLoaded"
              />
            </base-input>
          </div>
          <div class="col-lg-2 col-md-6">
            <base-input :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.PREPARATIONS_PERIOD')">
              <el-radio-group :value="view" @input="setView" class="period-toggle">
                <el-radio label="day">{{ $t("COMMON.DAY") }}</el-radio>
                <el-radio label="week">{{ $t("COMMON.WEEK") }}</el-radio>
              </el-radio-group>
            </base-input>
          </div>
          <div class="col-lg-3 col-md-6">
            <base-input :label="$t('COMMON.DATE')">
              <div class="d-flex align-items-center">
                <base-button
                  type="primary"
                  size="sm"
                  class="mr-1"
                  :title="$t('COMMON.PREV')"
                  @click="shiftPeriod(-1)"
                >
                  <i class="fas fa-chevron-left"></i>
                </base-button>
                <flat-picker
                  v-model="date"
                  :config="{ allowInput: true, locale: $flatPickrLocale() }"
                  class="form-control datepicker"
                />
                <base-button
                  type="primary"
                  size="sm"
                  class="ml-1"
                  :title="$t('COMMON.NEXT')"
                  @click="shiftPeriod(1)"
                >
                  <i class="fas fa-chevron-right"></i>
                </base-button>
              </div>
            </base-input>
          </div>
          <div class="col-lg-3 col-md-6">
            <div class="form-group">
              <!-- Empty label: keeps the text level with the inputs. -->
              <label class="form-control-label d-none d-lg-block">&nbsp;</label>
              <div class="period-summary">
                <strong>{{ periodLabel }}</strong>
                <span v-if="loading" class="text-muted small ml-2">
                  <i class="fas fa-spinner fa-spin mr-1"></i>
                  {{ $t("COMMON.LOADING") }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!production" class="alert alert-info mt-3">
          <i class="fas fa-industry mr-1"></i>
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.CHOOSE_PRODUCTION_ESTABLISHMENT") }}
        </div>

        <template v-else-if="!isWeekView">
          <!-- Aggregate across satellites: this is the kitchen production list. -->
          <div class="d-flex justify-content-between align-items-center flex-wrap mt-3 mb-2">
            <h4 class="mb-0">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.AGGREGATED_BY_PRODUCT") }}
            </h4>
            <base-button
              type="info"
              size="sm"
              :disabled="!dayProducts.length"
              @click="printProductionList"
            >
              <i class="fas fa-print mr-1"></i>
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRINT_PRODUCTION_LIST") }}
            </base-button>
          </div>
          <el-table
            class="table-responsive align-items-center table-flush"
            header-row-class-name="thead-light"
            :data="dayProducts"
          >
            <div slot="empty" class="text-center text-muted py-4">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PREPARATION_FOR_DAY") }}
            </div>
            <el-table-column :label="$t('COMMON.PRODUCT')" min-width="240">
              <template v-slot="{ row }">
                {{ row.name }}
                <div class="text-muted small">{{ row.code }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('COMMON.UNIT')" min-width="90">
              <template v-slot="{ row }">{{ row.unit || "-" }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.ORDERED')"
              min-width="120"
              class-name="number-column"
              label-class-name="number-column"
            >
              <template v-slot="{ row }">{{ row.ordered_quantity }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.REMAINING')"
              min-width="140"
              class-name="number-column"
              label-class-name="number-column"
            >
              <template v-slot="{ row }">{{ row.remaining_quantity }}</template>
            </el-table-column>
          </el-table>

          <!-- Then one block per satellite order, with its sending slip. -->
          <div v-for="order in payload.orders" :key="order.id" class="mt-5">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                {{ order.destination_establishment.company_name }}
                <span class="text-muted" v-if="order.destination_cafeteria.name">
                  — {{ order.destination_cafeteria.name }}
                </span>
                <badge type="secondary" class="ml-2">
                  {{ order.seq_number || order.code }}
                </badge>
                <badge v-if="order.day_fully_invoiced" type="success" class="ml-1">
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DAY_FULLY_INVOICED") }}
                </badge>
              </h4>
              <div>
                <base-button type="info" size="sm" class="mr-2" @click="printSlip(order)">
                  <i class="fas fa-print mr-1"></i>
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRINT_SENDING_SLIP") }}
                </base-button>
                <base-button type="secondary" size="sm" @click="viewOrder(order)">
                  <i class="fas fa-eye mr-1"></i>
                  {{ $t("COMMON.VIEW") }}
                </base-button>
              </div>
            </div>

            <el-table
              class="table-responsive align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="order.lines"
            >
              <el-table-column :label="$t('COMMON.PRODUCT')" min-width="240">
                <template v-slot="{ row }">
                  {{ row.name }}
                  <div class="text-muted small">{{ row.code }} {{ row.sku }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('COMMON.UNIT')" min-width="90">
                <template v-slot="{ row }">{{ row.unit || "-" }}</template>
              </el-table-column>
              <el-table-column
                :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.ORDERED')"
                min-width="110"
                class-name="number-column"
                label-class-name="number-column"
              >
                <template v-slot="{ row }">{{ row.ordered_quantity }}</template>
              </el-table-column>
              <el-table-column
                :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.REMAINING')"
                min-width="130"
                class-name="number-column"
                label-class-name="number-column"
              >
                <template v-slot="{ row }">{{ row.remaining_quantity }}</template>
              </el-table-column>
              <el-table-column
                :label="$t('COMMON.TOTAL')"
                min-width="120"
                class-name="number-column"
                label-class-name="number-column"
              >
                <template v-slot="{ row }">
                  {{ $formatCurrency(row.total_price) }}
                </template>
              </el-table-column>
            </el-table>

            <div class="text-right mt-2">
              <strong>{{ $t("COMMON.TOTAL") }} : {{ $formatCurrency(order.total) }}</strong>
            </div>
          </div>

          <div class="text-right mt-5" v-if="payload.orders.length">
            <h4>
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DAY_TOTAL") }} :
              {{ $formatCurrency(payload.total) }}
            </h4>
          </div>
        </template>

        <template v-else>
          <!-- Same kitchen list over the week, one column per ordered day. -->
          <div class="d-flex justify-content-between align-items-center flex-wrap mt-3 mb-2">
            <h4 class="mb-0">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.AGGREGATED_BY_PRODUCT") }}
            </h4>
            <base-button
              type="info"
              size="sm"
              :disabled="!weekProducts.length"
              @click="printProductionList"
            >
              <i class="fas fa-print mr-1"></i>
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRINT_PRODUCTION_LIST") }}
            </base-button>
          </div>
          <el-table
            class="table-responsive align-items-center table-flush"
            header-row-class-name="thead-light"
            :data="weekProducts"
          >
            <div slot="empty" class="text-center text-muted py-4">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PREPARATION_FOR_WEEK") }}
            </div>
            <el-table-column :label="$t('COMMON.PRODUCT')" min-width="200">
              <template v-slot="{ row }">
                {{ row.name }}
                <div class="text-muted small">{{ row.code }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('COMMON.UNIT')" min-width="80">
              <template v-slot="{ row }">{{ row.unit || "-" }}</template>
            </el-table-column>
            <el-table-column
              v-for="day in week.days"
              :key="day"
              :label="dayLabel(day)"
              min-width="105"
              class-name="number-column"
              label-class-name="number-column prep-day-header"
            >
              <template v-slot="{ row }">
                <span :class="{ 'text-muted': !row.quantities[day] }">
                  {{ row.quantities[day] || "-" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.TOTAL_QUANTITY')"
              min-width="110"
              class-name="number-column"
              label-class-name="number-column"
            >
              <template v-slot="{ row }">
                <strong>{{ row.total_quantity }}</strong>
              </template>
            </el-table-column>
          </el-table>

          <!-- Then one block per satellite order over the week. -->
          <div v-for="order in week.orders" :key="order.id" class="mt-5">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h4 class="mb-0">
                {{ order.destination_establishment.company_name }}
                <span class="text-muted" v-if="order.destination_cafeteria.name">
                  — {{ order.destination_cafeteria.name }}
                </span>
                <badge type="secondary" class="ml-2">
                  {{ order.seq_number || order.code }}
                </badge>
              </h4>
              <div>
                <base-button type="info" size="sm" class="mr-2" @click="printWeek(order)">
                  <i class="fas fa-print mr-1"></i>
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRINT_WEEK") }}
                </base-button>
                <base-button type="secondary" size="sm" @click="viewOrder(order)">
                  <i class="fas fa-eye mr-1"></i>
                  {{ $t("COMMON.VIEW") }}
                </base-button>
              </div>
            </div>

            <el-table
              class="table-responsive align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="sortedLines(order.lines)"
              show-summary
              :summary-method="(param) => orderWeekSummary(order, param)"
            >
              <el-table-column :label="$t('COMMON.PRODUCT')" min-width="200">
                <template v-slot="{ row }">
                  {{ row.name }}
                  <div class="text-muted small">{{ row.code }} {{ row.sku }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('COMMON.UNIT')" min-width="80">
                <template v-slot="{ row }">{{ row.unit || "-" }}</template>
              </el-table-column>
              <el-table-column
                v-for="day in week.days"
                :key="day"
                :label="dayLabel(day)"
                min-width="105"
                class-name="number-column"
                label-class-name="number-column prep-day-header"
              >
                <template v-slot:header>
                  {{ dayLabel(day) }}
                  <i
                    v-if="order.days[day] && order.days[day].non_received"
                    class="fas fa-ban text-warning ml-1"
                    :title="$t('ESTABLISHMENT_INTERNAL_ORDERS.DAY_NOT_RECEIVED')"
                  ></i>
                  <i
                    v-else-if="order.days[day] && order.days[day].has_invoice"
                    class="fas fa-check-circle text-success ml-1"
                    :title="$t('ESTABLISHMENT_INTERNAL_ORDERS.DAY_FULLY_INVOICED')"
                  ></i>
                </template>
                <template v-slot="{ row }">
                  <span :class="{ 'text-muted': !row.quantities[day] }">
                    {{ row.quantities[day] || "-" }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.TOTAL_QUANTITY')"
                min-width="100"
                class-name="number-column"
                label-class-name="number-column"
              >
                <template v-slot="{ row }">{{ row.total_quantity }}</template>
              </el-table-column>
            </el-table>

            <div class="text-right mt-2">
              <strong>{{ $t("COMMON.TOTAL") }} : {{ $formatCurrency(order.total) }}</strong>
            </div>
          </div>

          <div class="text-right mt-5" v-if="week.orders.length">
            <h4>
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK_TOTAL") }} :
              {{ $formatCurrency(week.total) }}
            </h4>
          </div>
        </template>
      </div>
    </card>
  </div>
</template>

<script>
import { Radio, RadioGroup, Table, TableColumn } from "element-ui";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { openPdfBlob } from "@/helpers/downloadPdf";
import ProductionEstablishmentSelector from "@/components/ProductionEstablishmentSelector.vue";

export default {
  name: "InternalPreparationsPage",
  components: {
    ProductionEstablishmentSelector,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    flatPicker,
  },
  data() {
    return {
      date: this.$route.query.date || moment().format("YYYY-MM-DD"),
      view: this.$route.query.view === "week" ? "week" : "day",
      // The production school whose list is shown: required, prefilled with the
      // user's own production establishment once the options are known.
      production: this.$route.query.production || null,
      loading: false,
      payload: { date: null, orders: [], products: [], total: 0 },
      week: {
        week_starts_at: null,
        week_ends_at: null,
        days: [],
        orders: [],
        products: [],
        totals_by_day: {},
        total: 0,
      },
    };
  },
  computed: {
    // A restricted user only picks among his own establishments.
    allowedProductionIds() {
      const me = this.$currentUser();
      if (!me?.restrict_to_establishments || !Array.isArray(me.allowedEstablishments)) {
        return null;
      }
      return me.allowedEstablishments.map((establishment) => establishment.id);
    },
    isWeekView() {
      return this.view === "week";
    },
    weekLabel() {
      if (!this.week.week_starts_at) {
        return "";
      }
      const from = moment(this.week.week_starts_at);
      const to = moment(this.week.week_ends_at);
      // Short when both days are in the same month: "du 21 au 25 septembre 2026".
      const fromFormat = from.isSame(to, "month") ? "D" : from.isSame(to, "year") ? "D MMMM" : "D MMMM YYYY";
      return this.$t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK_FROM_TO", {
        from: from.format(fromFormat),
        to: to.format("D MMMM YYYY"),
      });
    },
    periodLabel() {
      if (this.isWeekView) {
        return this.weekLabel;
      }
      const label = moment(this.date).format("dddd D MMMM YYYY");
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    weekProducts() {
      return this.sortedLines(this.week.products);
    },
    // Same order as the printed list (alphabetical, French collation).
    dayProducts() {
      return this.sortedLines(this.payload.products);
    },
  },
  created() {
    this.getPreparations();
  },
  watch: {
    date: {
      handler() {
        this.getPreparations();
      },
    },
    production() {
      this.getPreparations();
    },
  },
  methods: {
    onProductionsLoaded(establishments) {
      const allowed = this.allowedProductionIds;
      const options = (establishments || []).filter(
        (establishment) => establishment.active !== false && (!allowed || allowed.includes(establishment.id))
      );
      if (this.production && options.some((option) => option.id === this.production)) {
        return;
      }
      // Prefill with the first school the user may see (by name): his own one
      // when restricted, otherwise the first production school.
      this.production = options.length ? options[0].id : null;
    },
    setView(view) {
      if (this.view === view) return;
      this.view = view;
      this.getPreparations();
    },
    // The arrows move by one day or one week, following the period shown.
    shiftPeriod(offset) {
      this.date = moment(this.date)
        .add(offset, this.isWeekView ? "weeks" : "days")
        .format("YYYY-MM-DD");
    },
    dayLabel(day) {
      return moment(day).format("ddd D/MM");
    },
    sortedLines(lines) {
      return [...(lines || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr")
      );
    },
    orderWeekSummary(order, { columns }) {
      return columns.map((column, index) => {
        if (index === 0) {
          return this.$t("COMMON.TOTAL");
        }
        const day = this.week.days[index - 2];
        return day && order.days[day] ? this.$formatCurrency(order.days[day].total) : "";
      });
    },
    async getPreparations() {
      this.loading = true;
      const date = moment(this.date).format("YYYY-MM-DD");
      // Keep the view in the address so a refresh or a shared link reopens it.
      if (
        this.$route.query.date !== date ||
        (this.$route.query.view || "day") !== this.view ||
        (this.$route.query.production || null) !== this.production
      ) {
        const query = { ...this.$route.query, date, view: this.view, production: this.production || undefined };
        this.$router.replace({ query }).catch(() => {});
      }
      if (!this.production) {
        this.payload = { date: null, orders: [], products: [], total: 0 };
        this.week = { ...this.week, orders: [], products: [], totals_by_day: {}, total: 0 };
        this.loading = false;
        return;
      }
      try {
        if (this.isWeekView) {
          this.week = await this.$store.dispatch(
            "establishmentInternalOrders/weeklyPreparations",
            { date, sourceEstablishmentId: this.production }
          );
        } else {
          this.payload = await this.$store.dispatch(
            "establishmentInternalOrders/dailyPreparations",
            { date, sourceEstablishmentId: this.production }
          );
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
    async printSlip(order) {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch("establishmentInternalOrders/print", {
          id: order.id,
          date: moment(this.date).format("YYYY-MM-DD"),
        });
        openPdfBlob(
          response,
          `${order.seq_number || order.code}-${moment(this.date).format("YYYY-MM-DD")}.pdf`
        );
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    // PDF of the "Total par produit" table of the period on screen.
    async printProductionList() {
      const date = moment(this.date).format("YYYY-MM-DD");
      const filename = this.isWeekView
        ? `productions-semaine-${this.week.week_starts_at || date}.pdf`
        : `productions-${date}.pdf`;
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch(
          "establishmentInternalOrders/printPreparations",
          { date, view: this.view, sourceEstablishmentId: this.production }
        );
        openPdfBlob(response, filename);
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    async printWeek(order) {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        // No date: the whole ordered week, with its subtotal and rebate.
        const response = await this.$store.dispatch("establishmentInternalOrders/print", {
          id: order.id,
        });
        openPdfBlob(response, `${order.seq_number || order.code}.pdf`);
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    viewOrder(order) {
      this.$router.push({
        name: "View EstablishmentInternalOrder",
        params: { id: order.id },
      });
    },
    goToOrders() {
      this.$router.push({ name: "List EstablishmentInternalOrders" });
    },
  },
};
</script>

<style scoped>
/* Radios and the period text sit on the same line as the inputs (rem, not em:
   Element sets font-size: 0 on the radio group). */
.period-toggle,
.period-summary {
  min-height: calc(1.3125rem + 1.25rem + 2px);
  display: flex;
  align-items: center;
}

/* Element's cells break anywhere ("21/0" + "9"): only break between words. */
::v-deep .prep-day-header .cell {
  word-break: normal;
}
</style>
